/* eslint-disable import/prefer-default-export */
/**
 * PostHog feature-flag getters — the counterpart to growthbook.js.
 *
 * These historically lived in growthbook.js (some flags were migrated off
 * GrowthBook to PostHog with their keys unchanged), which made the file name
 * misleading. They read synchronously through PosthogService so call sites can
 * stay non-reactive; the flag keys themselves live in constants/ff.posthog.js.
 */
import { PosthogService } from './services/posthog';
import { FeatureFlagKeys } from './constants/ff.posthog';

/**
 * Migrated from Growthbook to PostHog (key unchanged) per GOTU-576.
 * Reads synchronously from PostHog to preserve the existing non-reactive
 * call-site behavior. Cleanup tracked in GOTU-3958.
 */
export const getShouldEnablePermanentJobV2 = () =>
  PosthogService.getInstance().isFeatureEnabled(
    FeatureFlagKeys.enablePermanentJobV2AdminPanel,
    false,
  );

/**
 * Gates the Best in Dental cycle admin: its two routes and its rail entry.
 *
 * Read synchronously, matching the Permanent Job V2 gate above — App.js decides
 * which routes to register during render, so an async read would leave a deep
 * link to fall through to the catch-all redirect before the flag arrived.
 *
 * Visibility only. awards-service gates the API on ADMIN_ALLOWLIST_USER_IDS, so
 * an admin with the flag on but not on the allowlist still gets 403s.
 */
export const getShouldEnableBestInDental = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.bestInDentalAdminPanel, false);

/**
 * Gates the GoTu Admin list: its route and its rail entry.
 *
 * Read synchronously, matching the two gates above — App.js decides which
 * routes to register during render, so an async read would leave a deep link to
 * fall through to the catch-all redirect before the flag arrived.
 *
 * Visibility only. `GET /auth-service/admin/internal-users` is already gated by
 * AdminAuthMiddleware, so the flag decides who sees the page, not who may read
 * the data — every admin can call that endpoint today with or without it.
 */
export const getShouldEnableGotuAdmins = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.gotuAdminsAdminPanel, false);

/**
 * Chooses where the DSO manager list comes from: on, the auth-service role
 * query; off, the Firebase-backed `dsoManagers` field of the monolith's
 * `admin/rams-dsomanagers`.
 *
 * Read only by `ensureManagerListSources`, which records the answer once per
 * session so the two sources stay behind one writer of `state.users.dsoManagers`.
 * UI releases here ride a numbered release train, so a bad role table in prod
 * needs a flag flip rather than a revert.
 *
 * Defaults off. Remove alongside the Firebase DSO_MANAGERS cleanup (GOTU-6387
 * follow-up), which is also when the fallback branch goes.
 */
export const getShouldSourceDsoManagersFromAuthService = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.dsoManagersFromAuthService, false);

/**
 * Chooses where the recruiter list comes from: on, the auth-service role query;
 * off, the hand-edited `PERMANENT_RECRUITER_IDS` Remote Config list resolved
 * through `useGetUsersByIds`. Sibling of the DSO manager flag above.
 *
 * This one needs its own flag rather than riding that one. The `recruiter` role
 * is newer than `dso` — product split "Recruiting / AE" into two roles — so an
 * unpopulated or half-populated role table is likelier here, and the three
 * consuming surfaces would degrade to a dropdown of only "All" with no error.
 * On the release train that is a revert plus a train, not a flip.
 *
 * Read through a module getter for symmetry with the DSO flag, even though
 * these callers are components; see `useGetRecruiters`.
 *
 * Defaults off. Remove alongside the Firebase PERMANENT_RECRUITER_IDS cleanup,
 * which is when `useGetUsersByIds` and `getPermanentRecruiterIds` go too.
 */
export const getShouldSourceRecruitersFromAuthService = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.recruitersFromAuthService, false);

/**
 * Chooses where the RAM list comes from: on, organization-service's sales
 * metros; off, the `ramUsers` field of the monolith's `admin/rams-dsomanagers`.
 * On also makes the office profile's RAM field read-only, since sales metros
 * are auto-assigned from the office's zip code.
 *
 * One flag for every surface that reads `state.users.ramUsers`, so the filters
 * and the profile field cannot disagree about which list is real. Read only by
 * `ensureManagerListSources`, which records the answer once per session.
 *
 * Defaults off. Must be fully on before the monolith stops serving `ramUsers`
 * (GOTU-7544), which is the off branch's only source.
 */
export const getShouldSourceRamsFromSalesMetros = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.salesMetrosAdminPanel, false);

/**
 * Chooses where the admin Referral List reads from: on, referral-service's
 * admin read endpoint; off, the monolith's `admin/referral` endpoint.
 *
 * Shared with the mobile Refer & Earn screen, so this key is not owned by
 * admin-portal alone — do not repurpose it. Defaults off, matching the
 * monolith behavior that shipped before referral-service existed, so an
 * admin whose flags have not loaded yet (or PostHog errors) still gets a
 * working list rather than a call to an endpoint that may not be deployed
 * to their environment yet.
 *
 * Read through a module getter, matching the DSO/recruiter flags above, so
 * the Redux thunk and the reducer can both call it outside React. Remove
 * alongside the monolith fallback path once every env is migrated.
 */
export const getShouldSourceReferralsFromReferralService = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.referralProgramRebuild, false);
